import type { FunctionComponent, PropsWithChildren } from 'react';
import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { OverlayAnchorPosition } from '../helpers/pcaOverlayFactory';
import { pcaOverlayFactory } from '../helpers/pcaOverlayFactory';

type MapOverlayContainer = PropsWithChildren<{
  position: google.maps.LatLng | google.maps.LatLngLiteral;
  pane?: keyof google.maps.MapPanes;
  map: google.maps.Map;
  minZoomLevelToDisplayOverlay?: number;
  overlayAnchorPosition?: OverlayAnchorPosition;
  classname?: string;
  isSelected?: boolean;
  isFocused?: boolean;
}>;

export const MAP_OVERLAY_SELECTED_CLASSNAME_MODIFIER = '--selected';
export const MAP_OVERLAY_FOCUSED_CLASSNAME_MODIFIER = '--focused';

export const MapOverlayContainer: FunctionComponent<MapOverlayContainer> = ({
  position,
  pane = 'markerLayer',
  map,
  children,
  minZoomLevelToDisplayOverlay,
  overlayAnchorPosition,
  classname,
  isSelected,
  isFocused,
}) => {
  const container = useMemo(() => {
    const div = document.createElement('div');
    div.style.position = 'absolute';
    if (classname) {
      div.classList.add(classname);
    }

    return div;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      e.stopPropagation();
    };

    const handleDoubleClick = (e: MouseEvent) => {
      e.stopPropagation();
    };

    container.addEventListener('click', handleClick);
    container.addEventListener('dblclick', handleDoubleClick);

    return () => {
      container.removeEventListener('click', handleClick);
      container.removeEventListener('dblclick', handleDoubleClick);
    };
  }, [container]);
  useEffect(() => {
    if (classname && container) {
      container.classList.toggle(
        `${classname}${MAP_OVERLAY_SELECTED_CLASSNAME_MODIFIER}`,
        isSelected,
      );
    }
  }, [classname, container, isSelected]);

  useEffect(() => {
    if (classname && container) {
      container.classList.toggle(
        `${classname}${MAP_OVERLAY_FOCUSED_CLASSNAME_MODIFIER}`,
        isFocused,
      );
    }
  }, [classname, container, isFocused]);

  const overlay = useMemo(() => {
    return pcaOverlayFactory(
      map,
      container,
      pane,
      position,
      minZoomLevelToDisplayOverlay,
      overlayAnchorPosition,
    );
  }, [container, map, minZoomLevelToDisplayOverlay, overlayAnchorPosition, pane, position]);

  useEffect(() => {
    overlay?.setMap(map);
    return () => overlay?.setMap(null);
  }, [map, overlay]);

  return createPortal(children, container);
};
